import { DetailDrawer } from "../components/DetailDrawer";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ammanStamp, relativeTime } from "../lib/dates";
import {
  fetchTeam, invitePerson, removePerson, resendInvite, setMemberPassword, setMemberRole,
} from "../lib/auth-api";
import { useAuth } from "../lib/auth-session";
import { invitableRoles, roleCan, ROLES, type Role, useRole } from "../lib/roles";
import { initials, useTeamStore, type TeamMember } from "../lib/team-store";
import { IconTeam } from "../components/icons";
import { PageSkeleton } from "../components/PageSkeleton";
import { AccessDenied } from "./NewsletterEditorPage";
import {
  EmptyState, PageHeader, Pill, PrimaryButton, SecondaryButton, SectionHeader, StatusBadge, Surface, Tag,
} from "../components/primitives";

const ROLE_TONE: Record<Role, "accent" | "purple" | "teal"> = {
  admin: "accent",
  editor: "purple",
  freelancer: "teal",
};

export function TeamPage() {
  const { role } = useRole();
  const { session } = useAuth();
  const live = !!session.me;
  const local = useTeamStore();
  const qc = useQueryClient();
  const remote = useQuery({
    queryKey: ["team"],
    queryFn: fetchTeam,
    enabled: live,
  });

  const members: TeamMember[] = live
    ? (remote.data?.members ?? []).map((m) => ({
      id: m.id, name: m.name, email: m.email, role: m.role, status: m.status,
      last_active: m.last_active, joined_at: m.joined_at, focus: m.focus ?? undefined,
    }))
    : local.members;
  const activity = live ? (remote.data?.activity ?? []) : local.activity;
  const ready = live ? !remote.isLoading : local.ready;

  const [filter, setFilter] = useState<"all" | Role | "invited">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const canInvite = roleCan(role, "team.invite");
  const allowedRoles = invitableRoles(role);
  const canManageRoles = role === "admin";
  const canSetPassword = roleCan(role, "team.password");

  const filtered = useMemo(() => {
    return members.filter((m) => {
      if (filter === "invited") return m.status === "invited";
      if (filter === "all") return true;
      return m.role === filter;
    });
  }, [members, filter]);

  const selected = members.find((m) => m.id === selectedId) ?? null;
  const admins = members.filter((m) => m.role === "admin").length;
  const empty = members.length === 0;

  async function onInvite(input: { name: string; email: string; role: Role; focus?: string }) {
    setFormError(null);
    if (live) {
      await invitePerson(input);
      await qc.invalidateQueries({ queryKey: ["team"] });
    } else {
      local.invite({ ...input, actor: role === "admin" ? "Admin" : "Editor" });
    }
  }

  if (!roleCan(role, "team.view")) return <AccessDenied need="Admins and editors" />;
  if (!ready) return <PageSkeleton />;

  return (
    <div style={{ display: "grid", gap: "var(--ui-space-4)" }}>
      <PageHeader
        title="Team"
        meta={live
          ? "Invite sends an email. They set a password and sign in."
          : "Demo list only — connect the database to send real invites."}
        actions={canInvite && !empty ? (
          <PrimaryButton onClick={() => { setInviteOpen(true); setFormError(null); }}>
            {role === "editor" ? "Add freelancer" : "Add person"}
          </PrimaryButton>
        ) : undefined}
      />

      {inviteOpen && canInvite && (
        <InviteForm
          allowedRoles={allowedRoles}
          live={live}
          error={formError}
          onClose={() => setInviteOpen(false)}
          onInvite={async (input) => {
            try {
              await onInvite(input);
              setInviteOpen(false);
            } catch (err) {
              setFormError(err instanceof Error ? err.message : "Could not send invite.");
            }
          }}
        />
      )}

      {empty && !inviteOpen ? (
        <Surface>
          <EmptyState
            icon={<IconTeam width={28} height={28} />}
            title="No one on the team yet"
            body={live
              ? "Send an invite. They get an email with a link to set a password and sign in."
              : "Add people here for layout only. Sign in with a connected workspace to email real invites."}
            hint={role === "editor"
              ? "Editors can add freelancers. Ask an admin to add other editors."
              : "Admins can add admins, editors, and freelancers."}
            actions={canInvite ? (
              <PrimaryButton onClick={() => setInviteOpen(true)}>
                {role === "editor" ? "Add freelancer" : "Add person"}
              </PrimaryButton>
            ) : undefined}
          />
        </Surface>
      ) : !empty && (
        <>
          <Surface style={{ overflow: "hidden" }}>
            <div className="nl-stat-row">
              <Mini n={members.length} label="People" />
              <Mini n={members.filter((m) => m.role === "editor").length} label="Editors" />
              <Mini n={members.filter((m) => m.role === "freelancer").length} label="Freelancers" />
              <Mini n={members.filter((m) => m.status === "invited").length} label="Invites pending" warn />
            </div>
          </Surface>

          <div className="nl-workspace">
            <Surface style={{ overflow: "hidden", minWidth: 0 }}>
              <div className="nl-toolbar">
                {(["all", "admin", "editor", "freelancer", "invited"] as const).map((key) => (
                  <Pill key={key} active={filter === key} onClick={() => setFilter(key)}>
                    {key === "all" ? "Everyone" : key === "invited" ? "Invited" : ROLES.find((r) => r.id === key)?.label}
                  </Pill>
                ))}
              </div>
              {filtered.length === 0 ? (
                <EmptyState
                  compact
                  title="No one in this filter"
                  body="Try another role, or add someone new."
                  hint="Invites stay pending until they set a password."
                />
              ) : (
                <div className="nl-team-grid">
                  {filtered.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className={`nl-card clickable${selected?.id === m.id ? " selected" : ""}`}
                      onClick={() => setSelectedId(m.id)}
                    >
                      <div className="nl-team-card-head">
                        <Avatar name={m.name} live={m.status === "active" && !!m.last_active} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: "var(--c-t1)" }}>{m.name}</div>
                          <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)", overflow: "hidden", textOverflow: "ellipsis" }}>{m.email}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                        <Tag tone={ROLE_TONE[m.role]}>{m.role}</Tag>
                        <StatusBadge tone={m.status === "active" ? "success" : "warn"}>{m.status}</StatusBadge>
                      </div>
                      <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t4)", marginTop: 10 }}>
                        {m.last_active ? `Active ${relativeTime(m.last_active)}` : "Hasn’t signed in yet"}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Surface>

            {selected && (
            <DetailDrawer label="Team member" onClose={() => setSelectedId(null)}>
            <MemberPanel
              key={selected?.id ?? "none"}
              member={selected}
              live={live}
              self={!!session.me && selected?.id === session.me.id}
              canManageRoles={canManageRoles}
              canSetPassword={canSetPassword}
              canRemove={!!selected && canInvite && allowedRoles.includes(selected.role)}
              canDemoteAdmin={!(selected?.role === "admin" && admins <= 1)}
              lastAdmin={selected?.role === "admin" && admins <= 1}
              onRole={async (next) => {
                if (!selected) return;
                if (live) {
                  await setMemberRole(selected.id, next);
                  await qc.invalidateQueries({ queryKey: ["team"] });
                } else local.setRole(selected.id, next);
              }}
              onActivate={() => selected && local.setStatus(selected.id, "active")}
              onResend={async () => {
                if (!selected) return;
                await resendInvite(selected.id);
              }}
              onSetPassword={async (password) => {
                if (!selected) return;
                await setMemberPassword(selected.id, password);
                await qc.invalidateQueries({ queryKey: ["team"] });
              }}
              onRemove={async () => {
                if (!selected) return;
                if (live) {
                  await removePerson(selected.id);
                  await qc.invalidateQueries({ queryKey: ["team"] });
                } else {
                  local.remove(selected.id, role === "admin" ? "Admin" : "Editor");
                }
                setSelectedId(null);
              }}
            />
            </DetailDrawer>
            )}
          </div>

          {activity.length > 0 && (
            <Surface>
              <SectionHeader title="Recent activity" meta="Invites, removals, role changes, and password resets" compact />
              <ol className="nl-activity">
                {activity.map((a) => (
                  <li key={a.id}>
                    <span className="nl-activity-dot" />
                    <div>
                      <div>{a.text}</div>
                      <div className="nl-activity-meta">{a.actor} · {ammanStamp(a.at)}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </Surface>
          )}
        </>
      )}
    </div>
  );
}

function Mini({ n, label, warn }: { n: number; label: string; warn?: boolean }) {
  return (
    <div className="nl-stat-mini">
      <div className="nl-stat-mini-label">{label}</div>
      <div className="nl-stat-mini-value tabnum" style={warn && n > 0 ? { color: "var(--c-warn-text)" } : undefined}>{n}</div>
    </div>
  );
}

function Avatar({ name, live }: { name: string; live?: boolean }) {
  return (
    <span className="nl-avatar">
      {initials(name)}
      {live && <span className="nl-presence" aria-label="Active" />}
    </span>
  );
}

function MemberPanel({
  member, live, self, canManageRoles, canSetPassword, canRemove, canDemoteAdmin, lastAdmin,
  onRole, onActivate, onResend, onSetPassword, onRemove,
}: {
  member: TeamMember | null;
  live: boolean;
  self: boolean;
  canManageRoles: boolean;
  canSetPassword: boolean;
  canRemove: boolean;
  canDemoteAdmin: boolean;
  lastAdmin: boolean;
  onRole: (role: Role) => void | Promise<void>;
  onActivate: () => void;
  onResend: () => Promise<void>;
  onSetPassword: (password: string) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwDone, setPwDone] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  if (!member) {
    return (
      <aside className="nl-detail-panel">
        <EmptyState
          compact
          title="Select a person"
          body="See their role, last activity, and what they can do."
          hint="Invites stay pending until they set a password."
        />
      </aside>
    );
  }
  return (
    <aside className="nl-detail-panel">
      <div className="nl-detail-panel-head">
        <div className="nl-team-card-head">
          <Avatar name={member.name} live={member.status === "active"} />
          <div>
            <div style={{ fontWeight: 600, fontSize: "var(--ui-text-section)" }}>{member.name}</div>
            <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)" }}>{member.email}</div>
          </div>
        </div>
      </div>
      <div className="nl-detail-panel-body">
        {canManageRoles ? (
          <label className="nl-field">
            <span>Role</span>
            <select
              className="nl-builder-select"
              value={member.role}
              onChange={(e) => void onRole(e.target.value as Role)}
              disabled={member.role === "admin" && !canDemoteAdmin}
            >
              {ROLES.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </label>
        ) : (
          <div>
            <div className="nl-stat-mini-label">Role</div>
            <div style={{ marginTop: 4 }}><Tag tone={ROLE_TONE[member.role]}>{member.role}</Tag></div>
          </div>
        )}
        <p style={{ margin: 0, fontSize: "var(--ui-text-meta)", color: "var(--c-t3)" }}>
          {member.role === "freelancer" && "Writes newsletters only. No segments, subscribers, or campaigns."}
          {member.role === "editor" && "Reviews content, builds audiences, schedules campaigns, and can add freelancers."}
          {member.role === "admin" && "Full access including team, data health, and settings."}
        </p>
        {member.focus && (
          <div>
            <div className="nl-stat-mini-label">Focus</div>
            <div style={{ marginTop: 4 }}>{member.focus}</div>
          </div>
        )}
        <dl className="nl-kv">
          <div><dt>Joined</dt><dd className="tabnum">{ammanStamp(member.joined_at)}</dd></div>
          <div><dt>Last active</dt><dd>{member.last_active ? relativeTime(member.last_active) : "—"}</dd></div>
        </dl>
        {live && member.status === "invited" && canInviteButton(canManageRoles, member.role) && (
          <SecondaryButton onClick={() => {
            setResendMsg(null);
            void onResend().then(() => setResendMsg("Invite email sent again.")).catch((err: Error) => setResendMsg(err.message));
          }}>Resend invite email</SecondaryButton>
        )}
        {resendMsg && <p style={{ margin: 0, fontSize: "var(--ui-text-meta)", color: "var(--c-t3)" }}>{resendMsg}</p>}
        {!live && member.status === "invited" && canManageRoles && (
          <SecondaryButton onClick={onActivate}>Mark as signed in</SecondaryButton>
        )}
        {live && canSetPassword && (
          <form
            className="nl-form"
            onSubmit={(e) => {
              e.preventDefault();
              setPwError(null);
              setPwDone(false);
              if (password.length < 8) {
                setPwError("Password must be at least 8 characters.");
                return;
              }
              if (password !== confirm) {
                setPwError("Passwords do not match.");
                return;
              }
              setPwBusy(true);
              void onSetPassword(password)
                .then(() => {
                  setPassword("");
                  setConfirm("");
                  setPwDone(true);
                })
                .catch((err: Error) => setPwError(err.message))
                .finally(() => setPwBusy(false));
            }}
          >
            <div className="nl-stat-mini-label">Set a new password</div>
            <p style={{ margin: "4px 0 8px", fontSize: "var(--ui-text-meta)", color: "var(--c-t3)" }}>
              You never see or type their current password. They can sign in with this immediately.
            </p>
            <label className="nl-field">
              <span>New password</span>
              <input className="nl-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            </label>
            <label className="nl-field">
              <span>Confirm</span>
              <input className="nl-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            </label>
            {pwError && <p className="nl-auth-error" role="alert">{pwError}</p>}
            {pwDone && <p style={{ margin: 0, fontSize: "var(--ui-text-meta)", color: "var(--c-success-text)" }}>Password saved. They can sign in now.</p>}
            <PrimaryButton type="submit" disabled={pwBusy}>{pwBusy ? "Saving…" : "Save password"}</PrimaryButton>
          </form>
        )}
        {canRemove && (
          <div style={{
            marginTop: 4,
            paddingTop: "var(--ui-space-4)",
            borderTop: "1px solid var(--c-border)",
            display: "grid",
            gap: "var(--ui-space-3)",
          }}>
            {self ? (
              <p style={{ margin: 0, fontSize: "var(--ui-text-meta)", color: "var(--c-t3)" }}>
                You cannot remove yourself.
              </p>
            ) : lastAdmin ? (
              <p style={{ margin: 0, fontSize: "var(--ui-text-meta)", color: "var(--c-t3)" }}>
                The last admin cannot be removed.
              </p>
            ) : confirmRemove ? (
              <>
                <p style={{ margin: 0, fontSize: "var(--ui-text-body)", lineHeight: 1.45, color: "var(--c-t2)" }}>
                  Remove {member.name} from the workspace? They will not be able to sign in.
                </p>
                {removeError && <p className="nl-auth-error" role="alert">{removeError}</p>}
                <div className="nl-action-row">
                  <SecondaryButton onClick={() => { setConfirmRemove(false); setRemoveError(null); }} disabled={removeBusy}>
                    Cancel
                  </SecondaryButton>
                  <button
                    type="button"
                    disabled={removeBusy}
                    onClick={() => {
                      setRemoveError(null);
                      setRemoveBusy(true);
                      void onRemove()
                        .catch((err: Error) => setRemoveError(err.message))
                        .finally(() => setRemoveBusy(false));
                    }}
                    style={{
                      height: "var(--ui-control-h-large)", padding: "0 14px",
                      borderRadius: "var(--ui-control-radius)", border: "none",
                      background: "var(--c-danger)", color: "#fff", fontWeight: 500,
                      fontSize: "var(--ui-text-control)", cursor: removeBusy ? "not-allowed" : "pointer",
                      opacity: removeBusy ? 0.5 : 1,
                    }}
                  >
                    {removeBusy ? "Removing…" : "Remove person"}
                  </button>
                </div>
              </>
            ) : (
              <SecondaryButton onClick={() => { setConfirmRemove(true); setRemoveError(null); }}>
                Remove person
              </SecondaryButton>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

function canInviteButton(canManage: boolean, memberRole: Role): boolean {
  return canManage || memberRole === "freelancer";
}

function InviteForm({
  allowedRoles, live, error, onClose, onInvite,
}: {
  allowedRoles: Role[];
  live: boolean;
  error: string | null;
  onClose: () => void;
  onInvite: (input: { name: string; email: string; role: Role; focus?: string }) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>(
    allowedRoles.includes("freelancer") ? "freelancer" : allowedRoles[0] ?? "freelancer",
  );
  const [focus, setFocus] = useState("");
  const [busy, setBusy] = useState(false);
  const options = ROLES.filter((r) => allowedRoles.includes(r.id));

  return (
    <Surface>
      <SectionHeader
        compact
        title={allowedRoles.length > 1 ? "Add to the workspace" : "Add a freelancer"}
        meta={live
          ? "They get an email from newsletter@altibbi.me with a link to set a password."
          : "Nothing is emailed until the workspace database is connected."}
      />
      <form
        className="nl-form"
        style={{ padding: "var(--ui-space-4)" }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim() || !allowedRoles.includes(role)) return;
          setBusy(true);
          void onInvite({ name, email, role, focus }).finally(() => setBusy(false));
        }}
      >
        <label className="nl-field">
          <span>Name</span>
          <input className="nl-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
        </label>
        <label className="nl-field">
          <span>Email</span>
          <input className="nl-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@altibbi.com" />
        </label>
        <label className="nl-field">
          <span>Role</span>
          <select className="nl-builder-select" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {options.map((r) => <option key={r.id} value={r.id}>{r.label} — {r.hint}</option>)}
          </select>
        </label>
        <label className="nl-field">
          <span>Focus <em>(optional)</em></span>
          <input className="nl-input" value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Nutrition, review, sending…" />
        </label>
        {error && <p className="nl-auth-error" role="alert">{error}</p>}
        <div className="nl-action-row" style={{ justifyContent: "flex-end" }}>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton type="submit" disabled={busy}>{busy ? "Sending…" : live ? "Send invite" : "Add person"}</PrimaryButton>
        </div>
      </form>
    </Surface>
  );
}
